import React, { useState } from "react";
import { Globe, Star, Shield, Rocket, CheckCircle, Send } from "../components/Icons";
import { Reveal, SectionHeading, PageGlow, Card } from "../components/ui";

/* ─── Pricing plans ─── */
const PLANS = [
  {
    id: "monthly",
    name: "Monthly",
    price: "$1,000",
    cadence: "/ month per app",
    note: "Billed monthly. Cancel anytime.",
    blurb: "Get your product featured on our developer page on a flexible month-to-month basis.",
    highlight: false,
  },
  {
    id: "6-month",
    name: "6 Months",
    price: "$5,000",
    cadence: "/ 6 months per app",
    note: "One-time payment — save $1,000.",
    blurb: "A half-year showcase slot at a reduced rate — ideal for established launches.",
    highlight: true,
  },
  {
    id: "12-month",
    name: "12 Months",
    price: "$8,000",
    cadence: "/ 12 months per app",
    note: "One-time payment — save $4,000.",
    blurb: "Our best value. A full year of credibility-building visibility for your product.",
    highlight: false,
  },
];

/* ─── Why list with us ─── */
const BENEFITS = [
  {
    icon: Shield,
    title: "Instant Credibility",
    desc: "Borrow the trust of an established developer profile with millions of installs across the Play Store.",
  },
  {
    icon: Globe,
    title: "Real Visibility",
    desc: "Your product is featured on our developer page, seen by an audience that already downloads and trusts our apps.",
  },
  {
    icon: Rocket,
    title: "Faster Traction",
    desc: "Skip the cold-start problem. Launch alongside proven products and inherit a head start on discovery.",
  },
  {
    icon: Star,
    title: "Curated Placement",
    desc: "A clean, professional listing that positions your product next to a portfolio of polished, live apps.",
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function HostingPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    brief: "",
    plan: "6-month",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const selectPlan = (id) => setForm((f) => ({ ...f, plan: id }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.brief) return;

    setLoading(true);

    const selectedPlan = PLANS.find((p) => p.id === form.plan);
    const planLabel = selectedPlan
      ? `${selectedPlan.name} — ${selectedPlan.price} ${selectedPlan.cadence}`
      : form.plan;

    // Hosting request — prepend the selected plan into the brief so it always
    // shows up in the email, regardless of the mail worker's template.
    const briefWithPlan = `[Product Hosting Request]\nSelected Plan: ${planLabel}\n\nDetails & Requirements:\n${form.brief}`;

    try {
      const response = await fetch("https://dowble-mail-worker.ram-tulugu.workers.dev", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          brief: briefWithPlan,
          plan: planLabel,
          formType: "hosting",
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitted(true);
      } else {
        alert(result.error || "Submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Form error:", error);
      alert("Network error. Unable to reach submission server.");
    } finally {
      setLoading(false);
    }
  };

  const emailValid = form.email === "" || EMAIL_RE.test(form.email);
  const canSubmit = form.name && form.email && EMAIL_RE.test(form.email) && form.brief;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 px-6 overflow-hidden">
        <PageGlow />
        <div className="relative max-w-4xl mx-auto text-center">
          <Reveal>
            <SectionHeading
              eyebrow="Get Featured · Developer Showcase"
              title={<>Get Featured on Our <span className="text-gradient-brand">Developer Page.</span></>}
              subtitle="We host your app or product on our established developer page — so you inherit the credibility, reach and trust we've built across millions of installs. A simple, proven way to give your product instant legitimacy."
            />
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <Reveal key={b.title}>
                <Card interactive className="h-full p-6">
                  <span className="w-11 h-11 rounded-xl bg-accent-500/10 border border-accent-500/20 flex items-center justify-center text-accent-600 mb-4">
                    <Icon size={20} />
                  </span>
                  <h3 className="text-zinc-900 font-bold text-base mb-1.5">{b.title}</h3>
                  <p className="text-zinc-600 text-sm leading-relaxed">{b.desc}</p>
                </Card>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Pricing"
              title={<>Simple, Per-App Pricing.</>}
              subtitle="Choose the plan that fits your runway. Tap a plan to pre-select it on the request form below."
            />
          </Reveal>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {PLANS.map((plan) => {
              const active = form.plan === plan.id;
              return (
                <Reveal key={plan.id}>
                  <button
                    type="button"
                    onClick={() => selectPlan(plan.id)}
                    aria-pressed={active}
                    className={`relative w-full h-full text-left rounded-3xl border p-8 transition-all duration-300 ${
                      active
                        ? "border-accent-500/60 bg-accent-500/[0.06] shadow-glow ring-1 ring-accent-500/30"
                        : "border-zinc-200 bg-white hover:border-zinc-300 hover:-translate-y-0.5"
                    }`}
                  >
                    {plan.highlight && (
                      <span className="absolute -top-3 left-8 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-accent-500 to-emerald-500 text-white shadow-sm">
                        Most Popular
                      </span>
                    )}
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-zinc-900 font-bold text-lg">{plan.name}</p>
                      <span
                        className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                          active ? "border-accent-500 bg-accent-500 text-white" : "border-zinc-300"
                        }`}
                      >
                        {active && <CheckCircle size={14} />}
                      </span>
                    </div>
                    <div className="flex items-end gap-1.5 mb-1">
                      <span className="text-zinc-900 font-bold text-4xl tracking-tight">{plan.price}</span>
                      <span className="text-zinc-500 text-sm mb-1.5">{plan.cadence}</span>
                    </div>
                    <p className="text-emerald-600 text-xs font-semibold mb-5">{plan.note}</p>
                    <p className="text-zinc-600 text-sm leading-relaxed">{plan.blurb}</p>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Request form */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          {submitted ? (
            <Reveal className="flex flex-col items-center justify-center text-center py-20 px-8 rounded-3xl border border-emerald-500/20 bg-emerald-500/[0.06] animate-scale-in">
              <span className="inline-flex w-16 h-16 rounded-2xl bg-emerald-500/12 border border-emerald-500/25 items-center justify-center text-emerald-600 mb-6">
                <CheckCircle size={36} />
              </span>
              <h2 className="text-2xl font-bold tracking-tight text-zinc-900 mb-3">Request Received.</h2>
              <p className="text-zinc-600 text-base max-w-md">
                Thank you, <strong className="text-zinc-900">{form.name}</strong>. We've received your hosting
                request and will follow up at <strong className="text-zinc-900">{form.email}</strong> shortly
                with the next steps to get your product listed.
              </p>
              <div className="mt-8 px-6 py-3 rounded-xl border border-zinc-200 bg-zinc-50">
                <p className="text-zinc-600 text-xs">Reference: DWB-{Date.now().toString(36).toUpperCase()}</p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form
                onSubmit={handleSubmit}
                className="rounded-3xl border border-zinc-200 bg-white backdrop-blur-sm p-8 space-y-6"
                aria-label="Product hosting request form"
              >
                <div>
                  <h2 className="text-zinc-900 font-bold text-xl mb-1 tracking-tight">Request a Listing</h2>
                  <p className="text-zinc-600 text-sm">
                    Fields marked <span className="text-rose-500">*</span> are required.
                  </p>
                </div>

                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="field-label">
                      Full Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Jane Doe"
                      className="field-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="field-label">
                      Email Address <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jane@company.com"
                      aria-invalid={!emailValid}
                      className={"field-input " + (!emailValid ? "border-rose-500/60 focus:border-rose-500/60 focus:ring-rose-500/25" : "")}
                    />
                    {!emailValid && (
                      <p className="text-rose-500 text-xs mt-1.5">Please enter a valid email address.</p>
                    )}
                  </div>
                </div>

                {/* Plan */}
                <div>
                  <label htmlFor="plan" className="field-label">Selected Plan</label>
                  <div className="relative">
                    <select
                      id="plan"
                      name="plan"
                      value={form.plan}
                      onChange={handleChange}
                      className="field-input appearance-none pr-10 cursor-pointer"
                    >
                      {PLANS.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} — {p.price} {p.cadence}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500">▾</span>
                  </div>
                </div>

                {/* Details / requirements */}
                <div>
                  <label htmlFor="brief" className="field-label">
                    Details &amp; Requirements <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    id="brief"
                    name="brief"
                    rows={6}
                    required
                    value={form.brief}
                    onChange={handleChange}
                    placeholder="Tell us about your product: what it is, the platform, store links if any, and what you'd like featured. Include any specific requirements for the listing."
                    className="field-input resize-none leading-relaxed"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || !canSubmit}
                  className="btn btn-primary w-full py-4 text-base"
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Hosting Request
                    </>
                  )}
                </button>

                <p className="text-zinc-500 text-xs text-center">
                  By submitting, you agree that we may contact you about hosting your product.
                  We never share your information with third parties.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
